import { Component, DestroyRef, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { first, firstValueFrom } from 'rxjs';

import { AlertsStore } from './alerts/alerts.store';
import { AUTH_CONFIG_IDS } from './auth/auth.config';
import { ScreenSizeOrientationLayerComponent } from './core/components/screen-size-orientation-layer/screen-size-orientation-layer.component';
import { SessionStore } from './core/services/session.store';
import { ConnectionStatusBannerComponent } from './shared/components/connection-status-banner/connection-status-banner.component';
import { useConnectionStatusBanner } from './shared/composables/use-connection-status-banner';
import { availableLocales, DEFAULT_LOCALE, EN_US_LOCALE } from './shared/i18n/i18n.config';
import { EndUserTokenPayload } from './shared/models/EndUserTokenPayload';
import { USER_TYPES, UserType } from './shared/models/User';
import { AnalyticsService } from './shared/services/analytics.service';
import { LocalStorageService } from './shared/services/localStorage.service';
import { SecurityService } from './shared/services/SecurityService';
import { setDefaultLocale } from './utils/l10n';
import { VideoCallsStore } from './video-conference/video-calls.store';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [ConnectionStatusBannerComponent, RouterOutlet, ScreenSizeOrientationLayerComponent],
})
export class AppComponent implements OnInit {
    protected readonly connectionBannerStatus = useConnectionStatusBanner().status;

    private readonly translateService = inject(TranslateService);
    private readonly localStorageService = inject(LocalStorageService);
    private readonly router = inject(Router);
    private readonly analytics = inject(AnalyticsService);
    private readonly session = inject(SessionStore);
    private readonly alertsStore = inject(AlertsStore);
    private readonly videoCalls = inject(VideoCallsStore);
    private readonly securityService = inject(SecurityService);
    private readonly oidcSecurityService = inject(OidcSecurityService);
    private readonly titleService = inject(Title);
    private readonly destroyRef = inject(DestroyRef);

    async ngOnInit() {
        this.followNavigation();

        const userType = this.getExpectedUserTypeFromCurrentPage();
        await this.setUserLanguage(userType);

        if (userType === USER_TYPES.INSTALLER && this.isInstallerPath()) {
            await this.loadInstallerEnvironment();
        }
    }

    private followNavigation() {
        const subscription = this.router.events.subscribe((event) => {
            // Old hashed links (/#/sites) open their page.
            if (event instanceof NavigationStart && event.url.startsWith('/#')) {
                void this.router.navigateByUrl(event.url.replace('/#', ''));
            }
            if (event instanceof NavigationEnd) {
                void this.analytics.notifyNavigation(event);
            }
        });
        this.destroyRef.onDestroy(() => subscription.unsubscribe());
    }

    private getExpectedUserTypeFromCurrentPage(): UserType {
        // i choose to use window.location rather than Angular's Router or ActivatedRoute
        // because this code seems to happen before redirection => router nor activatedRoute have '/admin' in their path/url/route
        const pathname = window.location.pathname;

        if (pathname.startsWith('/unauthorized')) {
            return USER_TYPES.ANONYMOUS;
        }

        if (pathname.startsWith('/admin')) {
            return USER_TYPES.ADMIN;
        }

        if (pathname.startsWith('/enduser') || pathname.startsWith('/query')) {
            return USER_TYPES.OWNER;
        }

        return USER_TYPES.INSTALLER;
    }

    private isInstallerPath(pathname: string = window.location.pathname): boolean {
        return (
            pathname === '/' ||
            pathname.startsWith('/sites') ||
            pathname.startsWith('/link-requests') ||
            pathname.startsWith('/alerts') ||
            pathname.startsWith('/resolve-alert') ||
            // Legacy links: read before the router redirects them to /notifications.
            pathname.startsWith('/messages') ||
            pathname.startsWith('/notifications') ||
            pathname.startsWith('/site') ||
            pathname.startsWith('/gateway')
        );
    }

    private async loadInstallerEnvironment(): Promise<void> {
        try {
            await firstValueFrom(this.securityService.isAuthenticated(AUTH_CONFIG_IDS.INSTALLER).pipe(first(Boolean)));
        } catch {
            return;
        }

        await this.session.load();

        this.alertsStore.initialize();
        void this.videoCalls.load();
    }

    private async setUserLanguage(userType: UserType) {
        this.translateService.setFallbackLang(DEFAULT_LOCALE);

        let userData: EndUserTokenPayload | null = null;
        if (this.isAuthenticatedUserType(userType)) {
            userData = await firstValueFrom(this.oidcSecurityService.getUserData(userType));
        }

        const locale = this.getLocale(userType, userData);

        this.translateService.use(locale);
        setDefaultLocale(locale);

        const title = await firstValueFrom(this.translateService.get('APP_PAGE_TITLE'));
        this.titleService.setTitle(`Serv-e-Go | ${title}`);
    }

    private getLocale(userType: UserType, userData: EndUserTokenPayload | null): string {
        let locale: string | undefined;

        if (userType === USER_TYPES.INSTALLER) {
            locale = this.localStorageService.getItem('locale');
        }

        return locale ?? this.getLocaleFromUserCountry(userData) ?? this.getLocaleFromBrowserLang() ?? DEFAULT_LOCALE;
    }

    private getLocaleFromUserCountry(userData: EndUserTokenPayload | null): string | undefined {
        const userCountry = userData?.cnty?.toUpperCase();
        return userCountry ? availableLocales.find((locale) => locale.endsWith(userCountry)) : undefined;
    }

    private getLocaleFromBrowserLang() {
        const preferredLocalByLang: Record<string, string> = { en: EN_US_LOCALE };
        const browserLang = this.translateService.getBrowserLang()?.split('-')[0];

        if (browserLang && preferredLocalByLang[browserLang]) {
            return preferredLocalByLang[browserLang];
        }

        return browserLang ? availableLocales.find((locale) => locale.startsWith(browserLang)) : undefined;
    }

    private isAuthenticatedUserType(userType: UserType): boolean {
        return Object.values(AUTH_CONFIG_IDS).includes(userType);
    }
}
